/**
 * The login page, replacing upstream's.
 *
 * UPSTREAM'S ASKS FOR A PASSWORD IT THEN THROWS AWAY. Its form collects an
 * email and password and hands them to `authProvider.login(params)` — and this
 * deployment's `login()` ignores params entirely and redirects to Keycloak.
 * So a user types their credentials, clicks Sign in, and is asked for the same
 * credentials again on a different page. Nothing is leaked (the values go into
 * a function that drops them), but a password field that does nothing is not a
 * thing to leave in front of people.
 *
 * It also links to "Forgot password?" -> /forgot-password, which is one of
 * upstream's Supabase pages. Submitting it reaches `ra-supabase-core`, which
 * builds a Supabase client from VITE_SUPABASE_URL — undefined here — and
 * throws. Passwords live in Keycloak; that link could never work.
 *
 * A BUTTON, NOT AN AUTOMATIC REDIRECT. Bouncing to Keycloak on mount is
 * slicker for a deep link, but it makes logging out impossible to observe:
 * react-admin sends you to /login after logout, and an auto-redirect would put
 * you straight back into the identity provider before you could see that
 * anything happened. One click, and what happens next is never a surprise.
 *
 * THE ONE EXCEPTION IS OPT-IN AND ONE-SHOT: `#/login?sso=1`. That is how the
 * portal's "Open CRM" card arrives — the person is already signed in to the
 * platform, so Keycloak completes silently and a button in between is just a
 * click for nothing. The flag is stripped from the URL before sign-in starts,
 * so everything that lands on the plain `#/login` (logout, a failed round
 * trip, the back button) still gets the button and can never loop. With a
 * CRM session already in this tab the flag goes straight to the dashboard.
 */
import { useEffect, useRef, useState } from "react";
import { useLogin, useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { Notification } from "@/components/admin/notification";
import { hasStoredSession } from "@/components/atomic-crm/providers/privateassistant/authProvider";

const SSO_FLAG = "sso";

/** True when the hash carries `?sso=1` (react-admin routes on the hash, so the
 *  query lives inside it: `#/login?sso=1`). */
const ssoRequested = (): boolean => {
  try {
    const h = window.location.hash || "";
    const q = h.indexOf("?");
    return q >= 0 && new URLSearchParams(h.slice(q + 1)).get(SSO_FLAG) === "1";
  } catch {
    return false;
  }
};

export const LoginPage = () => {
  const login = useLogin();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const autoStarted = useRef(false);

  const signIn = async () => {
    setBusy(true);
    try {
      // Never resolves on success: the browser navigates to Keycloak.
      await login({});
    } catch (e: any) {
      // Only reachable if the redirect could not even be built — a missing
      // config.json, say. Say so rather than leaving a dead button.
      setBusy(false);
      notify(e?.message || "Could not start sign-in", { type: "error" });
    }
  };

  useEffect(() => {
    if (autoStarted.current || !ssoRequested()) return;
    autoStarted.current = true;
    const base = window.location.pathname + window.location.search;
    if (hasStoredSession()) {
      // Already signed in here: the dashboard, not another round trip.
      // (Synchronous on purpose — an async token getter returns a Promise,
      // which is truthy, and that sent every fresh visitor here instead.)
      window.location.replace(`${base}#/`);
      return;
    }
    // Drop the flag first, so whatever comes back to this page comes back to
    // the button, not to another automatic redirect.
    try {
      window.history.replaceState(null, "", `${base}#/login`);
    } catch {
      // History API unavailable: the flag stays, and a failed round trip
      // would retry once more — still bounded by the ref above per mount.
    }
    void signIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">CRM</h1>
        <p className="text-sm text-muted-foreground">
          Sign in with your Private Assistant account. Your CRM is private to
          your organization.
        </p>
        <Button className="w-full cursor-pointer" onClick={signIn} disabled={busy}>
          {busy ? "Redirecting…" : "Sign in"}
        </Button>
        <p className="text-xs text-muted-foreground">
          Passwords are managed by your organization's sign-in, not here.
        </p>
      </div>
      <Notification />
    </div>
  );
};
